import { useState } from "react";
import type { AdminFeedbackListItemResponse } from "../../../types/adminTypes";
import { formatDateTime } from "../../../utils/DateUtils";
import Button from "../../ui/Button/Button";
import AdminPillBadge from "../ui/AdminPillBadge";
import AdminTableShell from "../ui/AdminTableShell";
import AdminFeedbackDetailModal from "./AdminFeedbackDetailModal";
import AdminFeedbackFilters from "./AdminFeedbackFilters";
import AdminFeedbackTableRow from "./AdminFeedbackTableRow";
import { adminFeedbackColumns } from "./adminFeedbackColumns";
import { getFeedbackRoleLabel, getFeedbackTypeLabel } from "./adminFeedbackUtils";

type AdminFeedbackTableProps = {
  feedback: AdminFeedbackListItemResponse[];
  isLoading: boolean;
  search: string;
  type: string;
  respondentRole: string;
  rating: string;
  createdFromDate: string;
  createdToDate: string;
  pageSize: number;
  page: number;
  resultsCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onSearchChange: (value: string) => void;
  onTypeChange: (value: string) => void;
  onRespondentRoleChange: (value: string) => void;
  onRatingChange: (value: string) => void;
  onCreatedFromDateChange: (value: string) => void;
  onCreatedToDateChange: (value: string) => void;
  onPageSizeChange: (value: number) => void;
  onPageChange: (nextPage: number) => void;
};

const AdminFeedbackTable = ({
  feedback,
  isLoading,
  search,
  type,
  respondentRole,
  rating,
  createdFromDate,
  createdToDate,
  pageSize,
  page,
  resultsCount,
  hasNextPage,
  hasPreviousPage,
  onSearchChange,
  onTypeChange,
  onRespondentRoleChange,
  onRatingChange,
  onCreatedFromDateChange,
  onCreatedToDateChange,
  onPageSizeChange,
  onPageChange,
}: AdminFeedbackTableProps) => {
  const [selectedFeedback, setSelectedFeedback] = useState<AdminFeedbackListItemResponse | null>(null);

  return (
    <>
      <AdminTableShell
        columns={adminFeedbackColumns}
        filters={
          <AdminFeedbackFilters
            search={search}
            type={type}
            respondentRole={respondentRole}
            rating={rating}
            createdFromDate={createdFromDate}
            createdToDate={createdToDate}
            pageSize={pageSize}
            onSearchChange={onSearchChange}
            onTypeChange={onTypeChange}
            onRespondentRoleChange={onRespondentRoleChange}
            onRatingChange={onRatingChange}
            onCreatedFromDateChange={onCreatedFromDateChange}
            onCreatedToDateChange={onCreatedToDateChange}
            onPageSizeChange={onPageSizeChange}
          />
        }
        mobileCards={feedback.map((item) => (
          <article key={item.id} className="rounded-[1.5rem] border border-myteengig-border bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="break-all font-semibold text-myteengig-text-primary">{item.email}</p>
                <p className="mt-1 line-clamp-2 text-sm text-myteengig-text-muted">{item.message}</p>
              </div>
              {item.rating !== null ? <AdminPillBadge tone="success">{item.rating} / 5</AdminPillBadge> : <AdminPillBadge>Not rated</AdminPillBadge>}
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Type</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <AdminPillBadge>{getFeedbackTypeLabel(item.type)}</AdminPillBadge>
                  <AdminPillBadge>{getFeedbackRoleLabel(item.respondentRole)}</AdminPillBadge>
                </div>
              </div>
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Gig</p>
                <p className="mt-1 text-sm text-myteengig-text-primary">{item.gigTitle ?? "Not tied to gig"}</p>
              </div>
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Submitted</p>
                <p className="mt-1 text-sm text-myteengig-text-primary">{formatDateTime(item.createdAt)}</p>
              </div>
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Source</p>
                <p className="mt-1 break-all text-sm text-myteengig-text-primary">{item.sourcePath ?? "Not provided"}</p>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => setSelectedFeedback(item)}>
                View
              </Button>
            </div>
          </article>
        ))}
        rows={feedback.map((item) => (
          <AdminFeedbackTableRow key={item.id} feedback={item} onView={setSelectedFeedback} />
        ))}
        isLoading={isLoading}
        isEmpty={!isLoading && feedback.length === 0}
        emptyMessage="No feedback found for the current filters."
        page={page}
        pageSize={pageSize}
        resultsCount={resultsCount}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onPageChange={onPageChange}
      />
      <AdminFeedbackDetailModal
        feedback={selectedFeedback}
        isOpen={selectedFeedback !== null}
        onClose={() => setSelectedFeedback(null)}
      />
    </>
  );
};

export default AdminFeedbackTable;
